import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import UserList from './UserList';
import { Cross2Icon, ArrowsIcon, HandleIcon } from '../icons/Icons';
import { getUser } from '../isdlData/matrixUserData';
import { DndContext, useDraggable, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSwappingStrategy, useSortable  } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function BuildableGolferList({ModalRef = null, ScrollRef = null, ListUpdated, GolferList, InputPlaceholder = "Search for a user"}){

    const [golfersSelected, setGolfersSelected] = useState([]);
    const [canEditUsers, setCanEditUsers] = useState(false);
    const [activeId, setActiveId] = useState(null);

      // These states will be used to position the overlay exactly at the pointer.
  const [initialPointer, setInitialPointer] = useState(null);
  const [pointerCoordinates, setPointerCoordinates] = useState({ x: 0, y: 0 });

    //Get data collector info
    useEffect(() => {
        const loadDCData = async () =>{
            const dcemail = sessionStorage.getItem('dataCollectorEmail');
    
            if(dcemail && isEmailCleanable(dcemail)){
                const cleanedEmail = cleanEmail(dcemail);
    
                const userData = await getUser(cleanedEmail.toLowerCase());
                if(userData.Role === "Admin" || userData.Role === "DataCollector"){
                    setCanEditUsers(true);
                }else{
                    setCanEditUsers(false);
                }
            }else{
                setCanEditUsers(false);
            }
        }
        loadDCData();

        return () => {

            };
        }, []);

      useEffect(() => {
        if (Array.isArray(GolferList)) {
          setGolfersSelected(GolferList);
        }
      }, [GolferList]);

    const handleGolferAdd = (golfer) =>{
        setGolfersSelected(prevGolfers => {
            if(golfer === null || golfer === undefined){
                return prevGolfers;
            }

            //May get rid of?
            // Check if the golfer is already in the list
            if (prevGolfers.some(existingGolfer => existingGolfer.id === golfer.id)) {
                return prevGolfers; // Return the same list if duplicate found (no update)
            }

            const updatedList = [...prevGolfers, golfer]; // Create a new array with the new golfer
            ListUpdated(updatedList); // Pass the new state to ListUpdated
            return updatedList;
        });
    };

    const handleGolferRemove = (golferId) => {
        setGolfersSelected(prevGolfers => {
            const updatedList = prevGolfers.filter(golfer => golfer.id !== golferId); // Remove golfer by ID
            ListUpdated(updatedList); // Pass the new state to ListUpdated
            return updatedList;
        });
    };

    // Drag event handlers
// When the drag starts, store the active id and the initial pointer coordinates.
const handleDragStart = (event) => {
  

    const { active, activatorEvent } = event;
    setActiveId(active.id);
    if (activatorEvent) {

      if(ScrollRef && ScrollRef.current){
        ScrollRef.current.style.overflow = "hidden";
      }

      // Handle both mouse and touch events:
      if (activatorEvent.clientX !== undefined) {
        const { clientX, clientY } = activatorEvent;
        setInitialPointer({ x: clientX, y: clientY });
        setPointerCoordinates({ x: clientX, y: clientY });
      } else if (activatorEvent.touches && activatorEvent.touches[0]) {
        const { clientX, clientY } = activatorEvent.touches[0];
        setInitialPointer({ x: clientX, y: clientY });
        setPointerCoordinates({ x: clientX, y: clientY });
      }
    }
  };

  // Update the pointer position as the drag moves.
  const handleDragMove = (event) => {
    if (initialPointer) {

      if(ScrollRef && ScrollRef.current){
        ScrollRef.current.style.overflow = "hidden";
      }

      setPointerCoordinates({
        x: initialPointer.x + event.delta.x,
        y: initialPointer.y + event.delta.y,
      });
    }
  };

  // When the drag ends, update the list order and clear the pointer data.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {

      if(ScrollRef && ScrollRef.current){
        ScrollRef.current.style.overflow = "auto";
      }

      const oldIndex = golfersSelected.findIndex(item => item.id === active.id);
      const newIndex = golfersSelected.findIndex(item => item.id === over.id);
      const newGolfersSelected = arrayMove(golfersSelected, oldIndex, newIndex);
      setGolfersSelected(newGolfersSelected);
      ListUpdated(newGolfersSelected);
    }
    setActiveId(null);
    setInitialPointer(null);
    setPointerCoordinates({ x: 0, y: 0 });
  };

    //Utils
    function isEmailCleanable(email) {
        // Regex to check if the email contains 'corp.' in the subdomain
        const corpDomainRegex = /^([^@]+)@corp\.(.+)$/;
        
        // Test if the email has 'corp.' in the domain part
        return corpDomainRegex.test(email);
    }

    function cleanEmail(email) {
        if (isEmailCleanable(email)) {
            // Remove 'corp.' from the domain
            return email.replace('corp.', '');
        }
        return email; // Return the original email if it's not cleanable
    }

    const dragDisabled = golfersSelected.length === 1;

    // Compute pointer coordinates relative to the modal container.
  let overlayStyle = {};
  if (ModalRef.current) {
    const modal = ModalRef.current.getBoundingClientRect();
    overlayStyle = {
      position: 'absolute',
      top: pointerCoordinates.y - modal.y,
      left: modal.x + 40,
      transform: 'translate(0px, -50%)',

    };
  } else {
    // Fallback: position relative to viewport.
    overlayStyle = {
      position: 'fixed',
      top: pointerCoordinates.y,
      left: pointerCoordinates.x,
      transform: 'translate(-50%, -50%)',
    };
  }

    return(
    <div style={{width:'100%', height:'100%', display:'flex', flexDirection:'column', justifyContent:'start'}}>
        <div style={{width:'100%', height:'50px'}}>
            <UserList dbInitialized={true} UserChanged={handleGolferAdd} HasEditPermission={canEditUsers} Placeholder={InputPlaceholder} />
        </div>
        <div className='golfer-list'>
            {/* Wrap your list with DndContext and SortableContext */}
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            // Use an array of IDs for the items
            items={golfersSelected.map((item) => item.id)}
            strategy={rectSwappingStrategy}
          >
            {golfersSelected.map((item, index) => (
              <SortableGolfer
                key={item.id}
                index={index}
                id={item.id}
                golfer={item}
                onRemove={handleGolferRemove}
                disabled={dragDisabled}
              />
            ))}
          </SortableContext>
          {/* DragOverlay renders the dragging item in a portal, outside the list */}
          <DragOverlay adjustScale={false} style={overlayStyle}>
            {activeId ? (
                <SortableGolfer
                id={activeId}
                golfer={golfersSelected.find(item => item.id === activeId)}
                // The overlay does not need the remove functionality.
                onRemove={() => {}}
                disabled={dragDisabled}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        {Array.isArray(golfersSelected) && golfersSelected.length === 0 && (
            <div style={{width:'100%', height:'50px', alignItems:'center', justifyContent:'center', display:'flex'}}>No golfers selected.</div>
        )}
        </div>
    </div>
    );
}

// A new component for sortable items using dnd-kit's useSortable hook
function SortableGolfer({ id, index = null, golfer, onRemove, disabled }) {
    // Pass the disabled flag to useSortable so that the item isn’t draggable when disabled.
  const { attributes, listeners, setNodeRef, transform, transition } =
  useSortable({ id, disabled });

const style = {
  transform: CSS.Transform.toString(transform),
  transition,
};

return (
  <div ref={setNodeRef} style={style} {...attributes} className="golfer-list-item">
    {/* Drag handle button */}
    <button
      className="golfer-list-item-move-button"
      disabled={disabled}
      // Only the drag handle gets the pointer events to start dragging.
      {...listeners}
      style={{
        cursor: disabled ? 'default' : 'grab',
      }}
    >
      <HandleIcon width='30px' height='30px' className={disabled ? 'icon-both-disabled' : 'icon-both'}/>
    </button>
    <div className="golfer-list-item-label" style={{ flexGrow: 1 }}>
      {((index != null && index !== undefined && !Number.isNaN(index) ) ? (index + 1) + ":\u00A0\u00A0\u00A0" : "") + golfer.FullName + ' (' + golfer.id + ')'}
    </div>
    <button
      className="golfer-list-item-remove-button"
      onClick={() => onRemove(golfer.id)}
      style={{
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <Cross2Icon width="30px" height="30px" />
    </button>
  </div>
);
  }

export default BuildableGolferList;
